import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFieldArray, useForm } from "react-hook-form";
import { AlertTriangle, Check, CheckCircle2, ChevronRight, Clock3, LoaderCircle, Plus, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { OPERATOR_AUTOMATION_BASE } from "../../app/moduleRegistry";
import { StatusBadge } from "../../components/StatusBadge";
import { availabilityRequestsApi } from "../../services/availabilityRequestsApi";
import { robotApi } from "../../services/robotApi";
import type { AvailabilityRequest, CreateRequestPayload } from "../../types";
import { useRobotOperation } from "./useRobotOperation";

const speeds = ["50 M", "100 M", "200 M", "500 M", "1 G", "2 G", "5 G", "10 G", "20 G", "50 G"] as const;
const contractTerms = ["Toistaiseksi voimassa oleva", "12 kk", "24 kk", "36 kk"] as const;
const defaultEmail = "operaattorimyynti@elmonet.fi";

const lineSchema = z.object({
  connectionType: z.literal("Ethernet etäliittymä"),
  speed: z.enum(speeds),
  contractTerm: z.enum(contractTerms),
  email: z.string().email("Syötä kelvollinen sähköpostiosoite")
});

const schema = z.object({
  streetAddress: z.string().trim().min(1, "Syötä katuosoite"),
  postalCode: z.string().regex(/^\d{5}$/, "Postinumeron pitää olla 5 numeroa"),
  city: z.string().trim().min(1, "Syötä postitoimipaikka"),
  contactName: z.string().trim().min(1, "Syötä Efecten saatavuuskyselyn tunnus"),
  lines: z.array(lineSchema).min(1, "Lisää vähintään yksi liittymärivi"),
  startImmediately: z.boolean()
}).superRefine((data, ctx) => {
  const seen = new Map<string, number>();
  data.lines.forEach((line, index) => {
    const key = `${line.speed}__${line.contractTerm}`;
    const firstIndex = seen.get(key);
    if (firstIndex !== undefined) {
      const message = `Rivi ${firstIndex + 1} käyttää jo yhdistelmää ${line.speed} / ${line.contractTerm}.`;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ["lines", index, "speed"]
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Valitse eri nopeus tai määräaikaisuus.",
        path: ["lines", index, "contractTerm"]
      });
    } else {
      seen.set(key, index);
    }
  });
});

type FormData = z.infer<typeof schema>;
type OperationNotice = {
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
};
const newLine = (): FormData["lines"][number] => ({
  connectionType: "Ethernet etäliittymä",
  speed: "100 M",
  contractTerm: "Toistaiseksi voimassa oleva",
  email: defaultEmail
});

export function NewRequestPage() {
  const [created, setCreated] = useState<AvailabilityRequest | null>(null);
  const [notice, setNotice] = useState<OperationNotice | null>(null);
  const [operationId, setOperationId] = useState<string>();
  const operation = useRobotOperation(operationId, setNotice);
  const liveRequest = useQuery({
    queryKey: ["request", created?.id, operation.data?.completed, operation.data?.status],
    queryFn: () => availabilityRequestsApi.get(created!.id),
    enabled: !!created?.id && !!operationId
  });
  const queryClient = useQueryClient();
  const {
    control, register, handleSubmit, watch,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      streetAddress: "",
      postalCode: "",
      city: "",
      contactName: "",
      lines: [newLine()],
      startImmediately: true
    }
  });
  const { fields, append, remove } = useFieldArray({ control, name: "lines" });
  const values = watch();

  const robotMutation = useMutation({
    mutationFn: (requestId: string) => robotApi.runOfferRobot(requestId),
    onMutate: () => {
      setNotice({
        type: "info",
        title: "Elisa-robotti käynnistyi",
        message: "Robotti kirjautuu Elisaan, perustaa kyselyn ja käsittelee liittymärivit yksi kerrallaan. Tunnisteet päivittyvät työjonoon."
      });
    },
    onSuccess: (result) => {
      setOperationId(result.operationId);
      setNotice({
        type: result.status === "authenticated" ? "success" : result.status === "queued" ? "info" : "warning",
        title: result.retryScheduled ? "Uusintayritys ajastettu" : result.status === "queued" ? "Robotti jonossa" : result.status === "authenticated" ? "Robotin ajo valmistui" : "Robotin ajo ei valmistunut",
        message: result?.message || "Elisa-robotti käsitteli kyselyn. Tarkista liittymäkohtaiset tilat avoimista kyselyistä."
      });
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["logs"] });
    },
    onError: (error) => {
      setNotice({
        type: "error",
        title: "Robotin ajo epäonnistui",
        message: error instanceof Error
          ? error.message
          : "Kysely tallennettiin työjonoon, mutta robotin automaattinen käynnistys epäonnistui."
      });
    }
  });

  const mutation = useMutation({
    mutationFn: availabilityRequestsApi.create,
    onMutate: () => {
      setNotice({
        type: "info",
        title: "Tallennetaan saatavuuskyselyä",
        message: "Luodaan kohde ja liittymäkohtaiset työjonorivit."
      });
    },
    onSuccess: (data) => {
      setCreated(data);
      setNotice({
        type: "success",
        title: "Saatavuuskysely tallennettu",
        message: `${data.jobs.length} liittymäkohtaista työtä luotiin tunnukselle ${data.requestCode}.`
      });
      const queuedId = data.jobs.find(job => job.robotOperationId)?.robotOperationId;
      if (queuedId) {
        setOperationId(queuedId);
        setNotice({ type: "info", title: "Robotti jonossa", message: "Kysely käsitellään taustalla. Voit sulkea sivun." });
      } else if (data.sendImmediately) robotMutation.mutate(data.id);
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["logs"] });
    },
    onError: (error) => {
      setNotice({
        type: "error",
        title: "Tallennus epäonnistui",
        message: error instanceof Error
          ? error.message
          : "Syöttämäsi tiedot säilyvät lomakkeella. Tarkista pakolliset kentät ja yritä uudelleen."
      });
    }
  });

  const onSubmit = (data: FormData) => mutation.mutate(data as CreateRequestPayload);

  if (created) return (<>
    <div className="success-view">
      <span className="success-view__icon"><Check /></span>
      <span className="kicker">SAATAVUUSKYSELY LUOTU</span>
      <h1>{created.requestCode}</h1>
      <p>{created.jobs.length} liittymäkohtaista työtä lisättiin työjonoon.</p>
      {notice && <div className={`operation-notice operation-notice--${notice.type}`}>
        {notice.type === "success" ? <CheckCircle2 /> : notice.type === "error" ? <AlertTriangle /> : notice.type === "warning" ? <AlertTriangle /> : <Clock3 />}
        <div><strong>{notice.title}</strong><span>{notice.message}</span></div>
        {(robotMutation.isPending || mutation.isPending) && <LoaderCircle className="spin operation-notice__spinner" />}
      </div>}
      <div className="created-jobs">
        {(liveRequest.data?.jobs ?? created.jobs).map((job) => (
          <div key={job.id}>
            <span className="speed-chip">{job.speed}</span>
            <span className="created-job__term">{job.contractTerm}</span>
            <StatusBadge status={job.status} />
          </div>
        ))}
      </div>
      <div className="button-row">
        <Link className="button button--primary" to={`${OPERATOR_AUTOMATION_BASE}/open`}>Siirry avoimiin kyselyihin <ChevronRight size={17} /></Link>
        <button className="button button--secondary" onClick={() => { setCreated(null); setOperationId(undefined); setNotice(null); }}>Luo toinen kysely</button>
      </div>
      {robotMutation.isPending && <div className="field-hint">Voit siirtyä työjonoon seuraamaan etenemistä. Sivua ei tarvitse pitää tässä näkymässä auki.</div>}
    </div>
  </>);

  return (
    <>
      <section className="page-heading">
        <div>
          <span className="kicker">SAATAVUUSKYSELYT / UUSI TYÖ</span>
          <h1>Uusi saatavuuskysely</h1>
          <p>Perusta kohde Efecte-tunnuksella ja lisää yksi tai useampi kysyttävä Ethernet-liittymä.</p>
        </div>
      </section>

      <form className="request-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-main">
          <section className="card form-section">
            <div className="section-title">
              <span>01</span>
              <div><h2>Kohde ja tunniste</h2><p>Nämä tiedot yhdistävät kaikki alla olevat liittymäkyselyt samaan kohteeseen</p></div>
            </div>
            <div className="form-grid">
              <Field label="Katuosoite" error={errors.streetAddress?.message} required full>
                <input placeholder="Esim. Linnankatu 12" autoComplete="street-address" {...register("streetAddress")} />
              </Field>
              <Field label="Postinumero" error={errors.postalCode?.message} required>
                <input inputMode="numeric" maxLength={5} placeholder="80330" autoComplete="postal-code" {...register("postalCode")} />
              </Field>
              <Field label="Postitoimipaikka" error={errors.city?.message} required>
                <input placeholder="Joensuu" autoComplete="address-level2" {...register("city")} />
              </Field>
              <Field label="Yhteyshenkilö / Efecte-tunnus" hint="Efecten saatavuuskyselyn tunnus toimii kohteen tunnisteena." error={errors.contactName?.message} required full>
                <input placeholder="Esim. ELMO-2026-003" {...register("contactName")} />
              </Field>
            </div>
          </section>

          <section className="card form-section">
            <div className="section-title section-title--actions">
              <span>02</span>
              <div><h2>Ethernet-etäliittymät</h2><p>Jokaisesta rivistä muodostetaan oma Elisa-kyselytyö</p></div>
              <button className="button button--secondary" type="button" onClick={() => append(newLine())}><Plus size={16} /> Lisää liittymä</button>
            </div>

            <div className="connection-lines">
              {fields.map((field, index) => (
                <article className="connection-line" key={field.id}>
                  <div className="connection-line__header">
                    <div><span className="connection-line__number">{index + 1}</span><div><strong>Ethernet etäliittymä</strong><small>Liittymäkohtaiset kyselytiedot</small></div></div>
                    {fields.length > 1 && <button type="button" onClick={() => remove(index)} aria-label={`Poista liittymärivi ${index + 1}`}><Trash2 /></button>}
                  </div>
                  <input type="hidden" {...register(`lines.${index}.connectionType`)} />
                  <div className="connection-line__fields">
                    <Field label="Nopeus" error={errors.lines?.[index]?.speed?.message} required>
                      <select {...register(`lines.${index}.speed`)}>
                        {speeds.map((speed) => <option key={speed}>{speed}</option>)}
                      </select>
                    </Field>
                    <Field label="Määräaikaisuus" error={errors.lines?.[index]?.contractTerm?.message} required>
                      <select {...register(`lines.${index}.contractTerm`)}>
                        {contractTerms.map((term) => <option key={term}>{term}</option>)}
                      </select>
                    </Field>
                    <Field label="Sähköpostiosoite" error={errors.lines?.[index]?.email?.message} required>
                      <input type="email" autoComplete="email" {...register(`lines.${index}.email`)} />
                    </Field>
                  </div>
                </article>
              ))}
            </div>
            {errors.lines?.root?.message && <span className="field-error">{errors.lines.root.message}</span>}
            <button className="add-line-button" type="button" onClick={() => append(newLine())}><Plus /> Lisää toinen Ethernet-liittymä</button>
          </section>
        </div>

        <aside className="form-aside">
          <div className="card sticky-summary">
            <span className="summary-eyebrow">KYSELYN YHTEENVETO</span>
            <h3>{values.contactName || "Uusi saatavuuskysely"}</h3>
            <dl>
              <div><dt>Kohde</dt><dd>{values.streetAddress || "Ei asetettu"}{values.city ? `, ${values.city}` : ""}</dd></div>
              <div><dt>Operaattori</dt><dd>Elisa</dd></div>
              <div><dt>Liittymiä</dt><dd>{fields.length}</dd></div>
              <div><dt>Ympäristö</dt><dd><span className="environment-label">Azure</span></dd></div>
            </dl>
            <div className="summary-lines">
              {values.lines?.map((line, index) => <div key={fields[index]?.id ?? index}><span>{index + 1}</span><strong>{line.speed}</strong><small>{line.contractTerm}</small></div>)}
            </div>
            <div className="process-hint">
              <strong>Mitä tapahtuu luonnin jälkeen?</strong>
              <ol>
                <li>Työt tallennetaan työjonoon.</li>
                <li>Robotti lähettää rivit Elisaan yksi kerrallaan.</li>
                <li>Elisa-tunnisteet ja seuraavat tarkistukset näkyvät Avoimet kyselyt -näkymässä.</li>
              </ol>
            </div>
            <label className="robot-start-toggle">
              <input type="checkbox" {...register("startImmediately")} />
              <span><strong>Käynnistä robotti heti</strong><small>Robotti käyttää tallennettuja teknisiä tunnuksia ja lukee vahvistuskoodin RPA-postilaatikosta.</small></span>
            </label>
            <div className="local-note"><p>Jokainen liittymärivi luo oman Elisa-kyselytyön saman Efecte-tunnuksen alle.</p></div>
            {notice && !created && <div className={`operation-notice operation-notice--${notice.type}`}>
              {notice.type === "success" ? <CheckCircle2 /> : notice.type === "error" ? <AlertTriangle /> : notice.type === "warning" ? <AlertTriangle /> : <Clock3 />}
              <div><strong>{notice.title}</strong><span>{notice.message}</span></div>
              {mutation.isPending && <LoaderCircle className="spin operation-notice__spinner" />}
            </div>}
            <button className="button button--primary button--wide" disabled={mutation.isPending} type="submit"><Send size={17} />{mutation.isPending ? "Luodaan töitä…" : `Luo ${fields.length} kyselytyö${fields.length === 1 ? "" : "tä"}`}</button>
          </div>
        </aside>
      </form>
    </>
  );
}

function Field({ label, error, hint, required, full, children }: {
  label: string; error?: string; hint?: string; required?: boolean; full?: boolean; children: React.ReactNode;
}) {
  return <label className={`field ${full ? "field--full" : ""}`}>
    <span>{label}{required && <em> *</em>}</span>
    {children}
    {hint && !error && <small className="field-hint">{hint}</small>}
    {error && <small className="field-error">{error}</small>}
  </label>;
}
